// src/components/generic/ImageCropEditor/ImageCropEditor.tsx
//
// Mobile-first image crop editor.
//
// UX model (matches iOS/Android native croppers):
// - The crop frame is FIXED in the center of the viewport
// - The user drags / pinch-zooms the IMAGE behind the frame
// - Dark overlay outside the crop area for clear visual feedback
// - Aspect ratio presets (Free, Square, 4:3, 3:4, 16:9) as pill buttons
// - Rotation buttons (90° CW / CCW)
// - "Skip" to use the original image without cropping
// - "Done" to crop and return the result
// - Rule-of-thirds grid lines appear during active gestures only
//
// Desktop: same model, mouse drag + scroll-wheel zoom.
//
// Pure canvas-based output — zero external dependencies.

import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Group,
  Text,
  ActionIcon,
  Button,
  SegmentedControl,
  Portal,
  Loader,
  Tooltip,
  Transition,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ImageCropEditorProps {
  /** Whether the editor is open */
  opened: boolean;

  /** Called when the user closes / cancels */
  onClose: () => void;

  /** Called with the cropped image blob */
  onConfirm: (blob: Blob) => void;

  /** Called when user taps "Skip" — returns original blob unchanged */
  onSkip?: (blob: Blob) => void;

  /** The source image to crop (Blob or object URL) */
  image: Blob | string | null;

  /**
   * Aspect ratio constraint for the crop frame.
   * - "free" — adaptive rectangle matching viewport orientation
   * - "square" — 1:1
   * - "4:3" — 4:3 landscape
   * - "3:4" — 3:4 portrait
   * - "16:9" — 16:9 landscape
   * - number — explicit width/height ratio (hides selector, locks ratio)
   *
   * Default: "free"
   */
  aspectRatio?: "free" | "square" | "4:3" | "3:4" | "16:9" | number;

  /** Whether to show the aspect ratio selector. Default: true */
  showAspectRatioSelector?: boolean;

  /** Whether to show the "Skip crop" button. Default: true */
  showSkip?: boolean;

  /** Whether to show rotation controls. Default: true */
  showRotation?: boolean;

  /** Max output dimension (width or height). Default: 1920 */
  maxOutputDimension?: number;

  /** JPEG quality for output (0–1). Default: 0.9 */
  outputQuality?: number;

  /** Title shown at the top. Default: "Crop Image" */
  title?: string;
}

// ============================================
// Aspect Ratio Presets
// ============================================

interface AspectPreset {
  value: string;
  label: string;
  ratio: number | null; // null = free
}

const ASPECT_PRESETS: AspectPreset[] = [
  { value: "free", label: "Free", ratio: null },
  { value: "square", label: "1:1", ratio: 1 },
  { value: "4:3", label: "4:3", ratio: 4 / 3 },
  { value: "3:4", label: "3:4", ratio: 3 / 4 },
  { value: "16:9", label: "16:9", ratio: 16 / 9 },
];

const parseAspectRatio = (
  value: "free" | "square" | "4:3" | "3:4" | "16:9" | number
): number | null => {
  if (typeof value === "number") return value;
  const preset = ASPECT_PRESETS.find((p) => p.value === value);
  return preset?.ratio ?? null;
};

// ============================================
// Constants
// ============================================

const DEFAULT_MAX_OUTPUT = 1920;
const DEFAULT_QUALITY = 0.9;

/** Maximum zoom multiplier over the minimum (cover) scale */
const MAX_ZOOM_MULTIPLIER = 5;

/** Padding around the crop frame (px) */
const FRAME_PADDING = 24;

/** Corner bracket length in pixels */
const CORNER_LEN = 20;
/** Corner bracket stroke width */
const CORNER_STROKE = 3;

/** Delay (ms) after gesture ends before grid fades out */
const GRID_FADE_DELAY_MS = 600;

// ============================================
// Geometry Helpers
// ============================================

/**
 * Compute crop frame dimensions fitting inside the available area.
 */
function computeCropFrame(
  areaW: number,
  areaH: number,
  ratio: number | null
): { width: number; height: number } {
  const maxW = areaW - FRAME_PADDING * 2;
  const maxH = areaH - FRAME_PADDING * 2;

  if (maxW <= 0 || maxH <= 0) return { width: 100, height: 100 };

  if (ratio == null) {
    // "Free" — use a rectangle roughly matching viewport orientation
    const freeRatio = areaW > areaH ? 4 / 3 : 3 / 4;
    let w = maxW;
    let h = w / freeRatio;
    if (h > maxH) { h = maxH; w = h * freeRatio; }
    return { width: Math.round(w), height: Math.round(h) };
  }

  let w = maxW;
  let h = w / ratio;
  if (h > maxH) { h = maxH; w = h * ratio; }
  return { width: Math.round(w), height: Math.round(h) };
}

/**
 * Minimum scale so the (rotated) image fully covers the crop frame.
 */
function computeMinScale(
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number,
  rotation: number
): number {
  const isRotated = rotation % 180 !== 0;
  const effW = isRotated ? imgH : imgW;
  const effH = isRotated ? imgW : imgH;
  return Math.max(frameW / effW, frameH / effH);
}

/**
 * Clamp pan offset so image always covers the crop frame.
 */
function clampOffset(
  ox: number,
  oy: number,
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number,
  scale: number,
  rotation: number
): { x: number; y: number } {
  const isRotated = rotation % 180 !== 0;
  const sW = (isRotated ? imgH : imgW) * scale;
  const sH = (isRotated ? imgW : imgH) * scale;
  const maxX = Math.max(0, (sW - frameW) / 2);
  const maxY = Math.max(0, (sH - frameH) / 2);
  return {
    x: Math.max(-maxX, Math.min(maxX, ox)),
    y: Math.max(-maxY, Math.min(maxY, oy)),
  };
}

function touchDist(t1: React.Touch, t2: React.Touch): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

// ============================================
// Canvas crop
// ============================================

async function cropToBlob(
  img: HTMLImageElement,
  frameW: number,
  frameH: number,
  scale: number,
  ox: number,
  oy: number,
  rotation: number,
  maxDim: number,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const isRotated = rotation % 180 !== 0;
    const srcW = isRotated ? img.height : img.width;
    const srcH = isRotated ? img.width : img.height;

    // Crop rectangle in unscaled, rotation-adjusted image space
    const cropLeft = (srcW * scale / 2 - ox - frameW / 2) / scale;
    const cropTop = (srcH * scale / 2 - oy - frameH / 2) / scale;
    const cropW = frameW / scale;
    const cropH = frameH / scale;

    // Output size (respect maxDim)
    let outW = Math.round(cropW);
    let outH = Math.round(cropH);
    if (outW > maxDim || outH > maxDim) {
      if (outW >= outH) {
        outH = Math.round((outH / outW) * maxDim);
        outW = maxDim;
      } else {
        outW = Math.round((outW / outH) * maxDim);
        outH = maxDim;
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) { resolve(null); return; }

    if (rotation !== 0) {
      // Draw rotated image on a temp canvas, then extract the crop
      const tmp = document.createElement("canvas");
      tmp.width = srcW;
      tmp.height = srcH;
      const tCtx = tmp.getContext("2d");
      if (!tCtx) { resolve(null); return; }
      tCtx.translate(srcW / 2, srcH / 2);
      tCtx.rotate((rotation * Math.PI) / 180);
      tCtx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.drawImage(tmp, cropLeft, cropTop, cropW, cropH, 0, 0, outW, outH);
    } else {
      ctx.drawImage(img, cropLeft, cropTop, cropW, cropH, 0, 0, outW, outH);
    }

    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

// ============================================
// CornerBrackets sub-component
// Four small absolutely-positioned SVGs (avoids
// calc() inside SVG path d="" which doesn't work).
// ============================================

const CornerBrackets: React.FC = React.memo(() => {
  const s = CORNER_STROKE;
  const len = CORNER_LEN;
  const size = len + s; // SVG canvas size

  const base: React.CSSProperties = {
    position: "absolute",
    width: size,
    height: size,
    overflow: "visible",
  };

  return (
    <>
      {/* Top-left */}
      <svg style={{ ...base, top: -s / 2, left: -s / 2 }} fill="none">
        <path
          d={`M ${s / 2} ${len} L ${s / 2} ${s / 2} L ${len} ${s / 2}`}
          stroke="white" strokeWidth={s} strokeLinecap="round"
        />
      </svg>

      {/* Top-right */}
      <svg style={{ ...base, top: -s / 2, right: -s / 2 }} fill="none">
        <path
          d={`M ${size - len} ${s / 2} L ${size - s / 2} ${s / 2} L ${size - s / 2} ${len}`}
          stroke="white" strokeWidth={s} strokeLinecap="round"
        />
      </svg>

      {/* Bottom-left */}
      <svg style={{ ...base, bottom: -s / 2, left: -s / 2 }} fill="none">
        <path
          d={`M ${s / 2} ${size - len} L ${s / 2} ${size - s / 2} L ${len} ${size - s / 2}`}
          stroke="white" strokeWidth={s} strokeLinecap="round"
        />
      </svg>

      {/* Bottom-right */}
      <svg style={{ ...base, bottom: -s / 2, right: -s / 2 }} fill="none">
        <path
          d={`M ${size - s / 2} ${size - len} L ${size - s / 2} ${size - s / 2} L ${size - len} ${size - s / 2}`}
          stroke="white" strokeWidth={s} strokeLinecap="round"
        />
      </svg>
    </>
  );
});

CornerBrackets.displayName = "CornerBrackets";

// ============================================
// GridLines sub-component (rule of thirds)
// ============================================

const GridLines: React.FC<{ visible: boolean }> = React.memo(({ visible }) => (
  <Box
    style={{
      position: "absolute",
      inset: 0,
      opacity: visible ? 1 : 0,
      transition: "opacity 250ms ease",
      pointerEvents: "none",
    }}
  >
    {[1, 2].map((i) => (
      <React.Fragment key={`g${i}`}>
        <Box
          style={{
            position: "absolute",
            left: `${(i / 3) * 100}%`,
            top: 0,
            bottom: 0,
            width: 1,
            backgroundColor: "rgba(255, 255, 255, 0.25)",
          }}
        />
        <Box
          style={{
            position: "absolute",
            top: `${(i / 3) * 100}%`,
            left: 0,
            right: 0,
            height: 1,
            backgroundColor: "rgba(255, 255, 255, 0.25)",
          }}
        />
      </React.Fragment>
    ))}
  </Box>
));

GridLines.displayName = "GridLines";

// ============================================
// Main Component
// ============================================

const ImageCropEditorComponent: React.FC<ImageCropEditorProps> = ({
  opened,
  onClose,
  onConfirm,
  onSkip,
  image,
  aspectRatio = "free",
  showAspectRatioSelector = true,
  showSkip = true,
  showRotation = true,
  maxOutputDimension = DEFAULT_MAX_OUTPUT,
  outputQuality = DEFAULT_QUALITY,
  title,
}) => {
  const { t } = useTranslation(["common"]);

  // ── Image loading ──

  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // ── Transform state ──

  const [selectedAspect, setSelectedAspect] = useState<string>(
    typeof aspectRatio === "number" ? "free" : aspectRatio
  );
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  // ── Grid visibility (show during gestures only) ──

  const [isGesturing, setIsGesturing] = useState(false);
  const gridTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showGrid = useCallback(() => {
    if (gridTimerRef.current) clearTimeout(gridTimerRef.current);
    setIsGesturing(true);
  }, []);

  const hideGridSoon = useCallback(() => {
    if (gridTimerRef.current) clearTimeout(gridTimerRef.current);
    gridTimerRef.current = setTimeout(() => setIsGesturing(false), GRID_FADE_DELAY_MS);
  }, []);

  useEffect(() => () => { if (gridTimerRef.current) clearTimeout(gridTimerRef.current); }, []);

  // ── Viewport measurement ──

  const vpRef = useRef<HTMLDivElement>(null);
  const [vpSize, setVpSize] = useState({ w: 0, h: 0 });

  // ── Gesture refs (mutable, no re-renders) ──

  const gRef = useRef({
    active: false,
    pinching: false,
    sx: 0, sy: 0,       // start client coords
    sox: 0, soy: 0,     // start offsets
    pDist: 0,            // start pinch distance
    pScale: 1,           // start scale at pinch begin
  });

  // ── Computed values ──

  const effectiveRatio = useMemo(() => {
    if (typeof aspectRatio === "number") return aspectRatio;
    return parseAspectRatio(selectedAspect as any);
  }, [aspectRatio, selectedAspect]);

  const cropFrame = useMemo(
    () => computeCropFrame(vpSize.w, vpSize.h, effectiveRatio),
    [vpSize.w, vpSize.h, effectiveRatio]
  );

  const minScale = useMemo(() => {
    if (!imgEl) return 1;
    return computeMinScale(
      imgEl.naturalWidth, imgEl.naturalHeight,
      cropFrame.width, cropFrame.height, rotation
    );
  }, [imgEl, cropFrame.width, cropFrame.height, rotation]);

  const maxScale = minScale * MAX_ZOOM_MULTIPLIER;
  const isReady = !!imgEl && vpSize.w > 0;

  // ── Load image when `image` prop or `opened` changes ──

  useEffect(() => {
    if (!opened || !image) {
      setImgEl(null);
      setImgUrl(null);
      return;
    }

    setIsLoadingImage(true);

    let url: string;
    let isObjUrl = false;

    if (typeof image === "string") {
      url = image;
    } else {
      url = URL.createObjectURL(image);
      isObjUrl = true;
    }

    setImgUrl(url);

    const img = new Image();
    img.onload = () => { setImgEl(img); setIsLoadingImage(false); };
    img.onerror = () => { setIsLoadingImage(false); };
    img.src = url;

    return () => { if (isObjUrl) URL.revokeObjectURL(url); };
  }, [opened, image]);

  // ── Reset transforms when image / frame / rotation change ──

  useEffect(() => {
    if (!imgEl) return;
    setScale(computeMinScale(
      imgEl.naturalWidth, imgEl.naturalHeight,
      cropFrame.width, cropFrame.height, rotation
    ));
    setOffsetX(0);
    setOffsetY(0);
  }, [imgEl, cropFrame.width, cropFrame.height, rotation]);

  // ── Full reset when opening ──

  useEffect(() => {
    if (opened) {
      setRotation(0);
      setScale(1);
      setOffsetX(0);
      setOffsetY(0);
      setSelectedAspect(typeof aspectRatio === "number" ? "free" : aspectRatio);
      setIsProcessing(false);
      setIsGesturing(false);
    }
  }, [opened, aspectRatio]);

  // ── Measure viewport ──

  useEffect(() => {
    if (!opened) return;
    const measure = () => {
      if (vpRef.current) {
        const r = vpRef.current.getBoundingClientRect();
        setVpSize({ w: r.width, h: r.height });
      }
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", measure); };
  }, [opened]);

  // ── Clamp helper ──

  const doClamp = useCallback(
    (nx: number, ny: number, s: number) => {
      if (!imgEl) return;
      const c = clampOffset(nx, ny, imgEl.naturalWidth, imgEl.naturalHeight, cropFrame.width, cropFrame.height, s, rotation);
      setOffsetX(c.x);
      setOffsetY(c.y);
    },
    [imgEl, cropFrame.width, cropFrame.height, rotation]
  );

  // ============================================
  // Touch handlers
  // ============================================

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    showGrid();
    const g = gRef.current;
    if (e.touches.length === 1) {
      g.active = true;
      g.pinching = false;
      g.sx = e.touches[0].clientX;
      g.sy = e.touches[0].clientY;
      g.sox = offsetX;
      g.soy = offsetY;
    } else if (e.touches.length === 2) {
      g.active = false;
      g.pinching = true;
      g.pDist = touchDist(e.touches[0], e.touches[1]);
      g.pScale = scale;
      g.sox = offsetX;
      g.soy = offsetY;
      g.sx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      g.sy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
    }
  }, [offsetX, offsetY, scale, showGrid]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    showGrid();
    const g = gRef.current;

    if (g.pinching && e.touches.length === 2) {
      const dist = touchDist(e.touches[0], e.touches[1]);
      const ns = Math.max(minScale, Math.min(maxScale, g.pScale * (dist / g.pDist)));
      setScale(ns);
      const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      doClamp(g.sox + mx - g.sx, g.soy + my - g.sy, ns);
    } else if (g.active && e.touches.length === 1) {
      doClamp(
        g.sox + e.touches[0].clientX - g.sx,
        g.soy + e.touches[0].clientY - g.sy,
        scale
      );
    }
  }, [minScale, maxScale, scale, doClamp, showGrid]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    hideGridSoon();
    const g = gRef.current;
    if (e.touches.length === 0) {
      g.active = false;
      g.pinching = false;
    } else if (e.touches.length === 1) {
      // Transitioned from pinch → single-finger
      g.pinching = false;
      g.active = true;
      g.sx = e.touches[0].clientX;
      g.sy = e.touches[0].clientY;
      g.sox = offsetX;
      g.soy = offsetY;
    }
  }, [offsetX, offsetY, hideGridSoon]);

  // ============================================
  // Mouse handlers (desktop)
  // ============================================

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    showGrid();
    const g = gRef.current;
    g.active = true;
    g.sx = e.clientX;
    g.sy = e.clientY;
    g.sox = offsetX;
    g.soy = offsetY;
  }, [offsetX, offsetY, showGrid]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const g = gRef.current;
    if (!g.active) return;
    showGrid();
    doClamp(g.sox + e.clientX - g.sx, g.soy + e.clientY - g.sy, scale);
  }, [scale, doClamp, showGrid]);

  const handleMouseUp = useCallback(() => {
    gRef.current.active = false;
    hideGridSoon();
  }, [hideGridSoon]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    showGrid();
    hideGridSoon();
    const factor = e.deltaY > 0 ? 0.95 : 1.05;
    const ns = Math.max(minScale, Math.min(maxScale, scale * factor));
    setScale(ns);
    doClamp(offsetX, offsetY, ns);
  }, [minScale, maxScale, scale, offsetX, offsetY, doClamp, showGrid, hideGridSoon]);

  // ============================================
  // Actions
  // ============================================

  const handleRotateCW = useCallback(() => setRotation((r) => (r + 90) % 360), []);
  const handleRotateCCW = useCallback(() => setRotation((r) => (r + 270) % 360), []);
  const handleAspectChange = useCallback((v: string) => setSelectedAspect(v), []);

  const handleResetZoom = useCallback(() => {
    setScale(minScale);
    setOffsetX(0);
    setOffsetY(0);
  }, [minScale]);

  const handleConfirm = useCallback(async () => {
    if (!imgEl) return;
    setIsProcessing(true);
    const blob = await cropToBlob(
      imgEl, cropFrame.width, cropFrame.height,
      scale, offsetX, offsetY, rotation,
      maxOutputDimension, outputQuality
    );
    setIsProcessing(false);
    if (blob) onConfirm(blob);
  }, [imgEl, cropFrame.width, cropFrame.height, scale, offsetX, offsetY, rotation, maxOutputDimension, outputQuality, onConfirm]);

  const handleSkip = useCallback(() => {
    if (!image || !onSkip) return;
    if (typeof image === "string") {
      fetch(image).then((r) => r.blob()).then((b) => onSkip(b)).catch(() => { });
    } else {
      onSkip(image);
    }
  }, [image, onSkip]);

  // ============================================
  // Segment data for aspect ratio selector
  // ============================================

  const aspectData = useMemo(
    () => ASPECT_PRESETS.map((p) => ({ value: p.value, label: p.label })),
    []
  );

  // ============================================
  // Styles (memoized)
  // ============================================

  const imgStyle = useMemo((): React.CSSProperties => {
    if (!imgEl) return {};
    return {
      position: "absolute",
      left: "50%",
      top: "50%",
      transformOrigin: "center center",
      transform: `translate(-50%, -50%) translate(${offsetX}px, ${offsetY}px) scale(${scale}) rotate(${rotation}deg)`,
      width: imgEl.naturalWidth,
      height: imgEl.naturalHeight,
      maxWidth: "none",
      maxHeight: "none",
      userSelect: "none",
      pointerEvents: "none",
      willChange: "transform",
    };
  }, [imgEl, offsetX, offsetY, scale, rotation]);

  const overlayStyle = useMemo((): React.CSSProperties => {
    if (vpSize.w === 0) return {};
    const fl = (vpSize.w - cropFrame.width) / 2;
    const ft = (vpSize.h - cropFrame.height) / 2;
    const fr = fl + cropFrame.width;
    const fb = ft + cropFrame.height;
    return {
      position: "absolute",
      inset: 0,
      backgroundColor: "rgba(0, 0, 0, 0.55)",
      clipPath: `polygon(
        0% 0%, 0% 100%,
        ${fl}px 100%, ${fl}px ${ft}px,
        ${fr}px ${ft}px, ${fr}px ${fb}px,
        ${fl}px ${fb}px, ${fl}px 100%,
        100% 100%, 100% 0%
      )`,
      pointerEvents: "none",
      zIndex: 2,
    };
  }, [vpSize, cropFrame]);

  const frameStyle = useMemo((): React.CSSProperties => ({
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: cropFrame.width,
    height: cropFrame.height,
    pointerEvents: "none",
    zIndex: 3,
  }), [cropFrame]);

  // ============================================
  // Render
  // ============================================

  if (!opened) return null;

  const displayTitle = title || t("common:imageCrop.title", { defaultValue: "Crop Image" });

  return (
    <Portal>
      <Transition mounted={opened} transition="fade" duration={200}>
        {(transStyles) => (
          <Box
            style={{
              ...transStyles,
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              backgroundColor: "#000",
              display: "flex",
              flexDirection: "column",
              touchAction: "none",
              overscrollBehavior: "none",
            }}
          >
            {/* ═══════ Top bar ═══════ */}
            <Box
              style={{
                position: "relative",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                paddingTop: "max(8px, env(safe-area-inset-top))",
                minHeight: 48,
              }}
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                onClick={onClose}
                aria-label={t("common:actions.close", { defaultValue: "Close" })}
                styles={{ root: { color: "white", minWidth: 44, minHeight: 44 } }}
              >
                <AppIcon iconName="X" size={22} />
              </ActionIcon>

              <Text
                size="sm"
                fw={600}
                c="white"
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}
              >
                {displayTitle}
              </Text>

              <Tooltip
                label={t("common:imageCrop.resetZoom", { defaultValue: "Reset" })}
                position="bottom"
                withArrow
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={handleResetZoom}
                  disabled={!isReady}
                  aria-label={t("common:imageCrop.resetZoom", { defaultValue: "Reset zoom" })}
                  styles={{ root: { color: "white", minWidth: 44, minHeight: 44 } }}
                >
                  <AppIcon iconName="FocusCentered" size={22} />
                </ActionIcon>
              </Tooltip>
            </Box>

            {/* ═══════ Viewport (image + overlay + crop frame) ═══════ */}
            <Box
              ref={vpRef}
              style={{
                flex: 1,
                position: "relative",
                overflow: "hidden",
                cursor: isReady ? "grab" : "default",
                touchAction: "none",
              }}
              onTouchStart={isReady ? handleTouchStart : undefined}
              onTouchMove={isReady ? handleTouchMove : undefined}
              onTouchEnd={isReady ? handleTouchEnd : undefined}
              onMouseDown={isReady ? handleMouseDown : undefined}
              onMouseMove={isReady ? handleMouseMove : undefined}
              onMouseUp={isReady ? handleMouseUp : undefined}
              onMouseLeave={isReady ? handleMouseUp : undefined}
              onWheel={isReady ? handleWheel : undefined}
            >
              {/* Loading spinner */}
              {isLoadingImage && (
                <Box
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 5,
                  }}
                >
                  <Loader color="white" size="md" />
                </Box>
              )}

              {/* Image */}
              {imgUrl && imgEl && (
                <img src={imgUrl} alt="" style={imgStyle} draggable={false} />
              )}

              {/* Dark overlay with transparent crop hole */}
              {isReady && <Box style={overlayStyle} />}

              {/* Crop frame: border + grid + corners */}
              {isReady && (
                <Box style={frameStyle}>
                  <Box
                    style={{
                      position: "absolute",
                      inset: 0,
                      border: "1px solid rgba(255, 255, 255, 0.35)",
                    }}
                  />
                  <GridLines visible={isGesturing} />
                  <CornerBrackets />
                </Box>
              )}
            </Box>

            {/* ═══════ Controls (aspect ratio + rotation) ═══════ */}
            {isReady && (showAspectRatioSelector || showRotation) && (
              <Box
                style={{
                  position: "relative",
                  zIndex: 10,
                  padding: "10px 16px 4px",
                }}
              >
                <Group justify="center" gap="sm" wrap="nowrap">
                  {showRotation && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      onClick={handleRotateCCW}
                      aria-label={t("common:imageCrop.rotateCCW", { defaultValue: "Rotate left" })}
                      styles={{ root: { color: "white", minWidth: 44, minHeight: 44 } }}
                    >
                      <AppIcon iconName="Rotate" size={20} />
                    </ActionIcon>
                  )}

                  {showAspectRatioSelector && typeof aspectRatio !== "number" && (
                    <SegmentedControl
                      value={selectedAspect}
                      onChange={handleAspectChange}
                      data={aspectData}
                      size="xs"
                      styles={{
                        root: {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                        },
                        label: {
                          color: "rgba(255, 255, 255, 0.7)",
                          fontSize: 12,
                          fontWeight: 500,
                          paddingLeft: 10,
                          paddingRight: 10,
                          minHeight: 32,
                          display: "flex",
                          alignItems: "center",
                        },
                        indicator: {
                          backgroundColor: "white",
                        },
                      }}
                    />
                  )}

                  {showRotation && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      onClick={handleRotateCW}
                      aria-label={t("common:imageCrop.rotateCW", { defaultValue: "Rotate right" })}
                      styles={{ root: { color: "white", minWidth: 44, minHeight: 44 } }}
                    >
                      <AppIcon iconName="RotateClockwise" size={20} />
                    </ActionIcon>
                  )}
                </Group>
              </Box>
            )}

            {/* ═══════ Action buttons ═══════ */}
            <Box
              style={{
                position: "relative",
                zIndex: 10,
                padding: "10px 24px",
                paddingBottom: "max(16px, env(safe-area-inset-bottom))",
              }}
            >
              <Group justify="center" gap="md">
                {showSkip && onSkip && (
                  <Button
                    variant="subtle"
                    color="gray"
                    size="lg"
                    onClick={handleSkip}
                    disabled={isProcessing || !isReady}
                    styles={{
                      root: {
                        color: "white",
                        minHeight: 48,
                        paddingLeft: 20,
                        paddingRight: 20,
                      },
                    }}
                    leftSection={<AppIcon iconName="ArrowRight" size={18} />}
                  >
                    {t("common:imageCrop.skip", { defaultValue: "Skip" })}
                  </Button>
                )}

                <Button
                  variant="filled"
                  size="lg"
                  onClick={handleConfirm}
                  disabled={isProcessing || !isReady}
                  loading={isProcessing}
                  leftSection={
                    !isProcessing ? <AppIcon iconName="Check" size={18} /> : undefined
                  }
                  styles={{
                    root: {
                      minHeight: 48,
                      paddingLeft: 24,
                      paddingRight: 24,
                    },
                  }}
                >
                  {t("common:imageCrop.done", { defaultValue: "Done" })}
                </Button>
              </Group>
            </Box>
          </Box>
        )}
      </Transition>
    </Portal>
  );
};

export const ImageCropEditor = React.memo(ImageCropEditorComponent);